import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {z as zod} from "zod";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";

export enum ProductCatalogueTemplateAdditionalColumnType {
    email = "0eeef303-84d6-4163-a323-e0ef4c8a0468",
    indianPhoneNumber = "144f6c72-a1d9-49d2-accb-a828d6a5852e",
    number = "61ec7adb-4554-4271-957f-9394ace09539",
    // Retired after the select-options-to-UDO migration: select = "0a3d0906-0462-41bc-99ce-7d74b5d2eed3",
    text = "ef46fdb3-34d5-418a-8382-ff6b2f18649f",
    unknown = "d152fb65-0a97-45be-89d1-8c8c5ff20778",
    userDefinedObject = "645809c4-2065-495b-80c5-ac5ba07e1f2f",
    userDefinedObjects = "535c27c7-8957-42f0-95f4-26191a1d260e",
    uuid = "0b45d4dd-1cfb-4bfb-8fb4-7ce04d38cbd2",
};

export type ProductCatalogueTemplateAdditionalColumnConfigurationMap = {
    [ProductCatalogueTemplateAdditionalColumnType.email]: {required: boolean};
    [ProductCatalogueTemplateAdditionalColumnType.indianPhoneNumber]: {required: boolean};
    [ProductCatalogueTemplateAdditionalColumnType.number]: {required: boolean};
    [ProductCatalogueTemplateAdditionalColumnType.text]: {required: boolean};
    [ProductCatalogueTemplateAdditionalColumnType.unknown]: {required: boolean};
    [ProductCatalogueTemplateAdditionalColumnType.userDefinedObject]: {
        required: boolean;
        userDefinedObjectTemplateId: Uuid;
    };
    [ProductCatalogueTemplateAdditionalColumnType.userDefinedObjects]: {
        required: boolean;
        userDefinedObjectTemplateId: Uuid;
    };
    [ProductCatalogueTemplateAdditionalColumnType.uuid]: {required: boolean};
};

export type ProductCatalogueTemplateAdditionalColumn = {
    displayName: string;
    id: Uuid;
} & {
    [K in ProductCatalogueTemplateAdditionalColumnType]: {
        configuration: ProductCatalogueTemplateAdditionalColumnConfigurationMap[K];
        type: K;
    };
}[ProductCatalogueTemplateAdditionalColumnType];

const productCatalogueTemplateAdditionalColumnBaseValidator = zod.object({
    displayName: zod.string(),
    id: uuidZodValidator,
});

export const productCatalogueTemplateAdditionalColumnsZodValidator: zod.ZodType<ProductCatalogueTemplateAdditionalColumn> = zod.discriminatedUnion("type", [
    productCatalogueTemplateAdditionalColumnBaseValidator.extend({
        configuration: zod.object({required: zod.boolean()}),
        type: zod.literal(ProductCatalogueTemplateAdditionalColumnType.uuid),
    }),
    productCatalogueTemplateAdditionalColumnBaseValidator.extend({
        configuration: zod.object({required: zod.boolean()}),
        type: zod.literal(ProductCatalogueTemplateAdditionalColumnType.email),
    }),
    productCatalogueTemplateAdditionalColumnBaseValidator.extend({
        configuration: zod.object({required: zod.boolean()}),
        type: zod.literal(ProductCatalogueTemplateAdditionalColumnType.indianPhoneNumber),
    }),
    productCatalogueTemplateAdditionalColumnBaseValidator.extend({
        configuration: zod.object({required: zod.boolean()}),
        type: zod.literal(ProductCatalogueTemplateAdditionalColumnType.number),
    }),
    productCatalogueTemplateAdditionalColumnBaseValidator.extend({
        configuration: zod.object({required: zod.boolean()}),
        type: zod.literal(ProductCatalogueTemplateAdditionalColumnType.text),
    }),
    productCatalogueTemplateAdditionalColumnBaseValidator.extend({
        configuration: zod.object({
            required: zod.boolean(),
            userDefinedObjectTemplateId: uuidZodValidator,
        }),
        type: zod.literal(ProductCatalogueTemplateAdditionalColumnType.userDefinedObject),
    }),
    productCatalogueTemplateAdditionalColumnBaseValidator.extend({
        configuration: zod.object({
            required: zod.boolean(),
            userDefinedObjectTemplateId: uuidZodValidator,
        }),
        type: zod.literal(ProductCatalogueTemplateAdditionalColumnType.userDefinedObjects),
    }),
    productCatalogueTemplateAdditionalColumnBaseValidator.extend({
        configuration: zod.object({required: zod.boolean()}),
        type: zod.literal(ProductCatalogueTemplateAdditionalColumnType.unknown),
    }),
]);

// function getSchemaPropertyTypeFromProductCatalogueTemplateAdditionalColumnType(additionalColumnType: ProductCatalogueTemplateAdditionalColumnType): SchemaPropertyType {
//     switch (additionalColumnType) {
//         case ProductCatalogueTemplateAdditionalColumnType.email: {
//             return SchemaPropertyType.string;
//         }
//         case ProductCatalogueTemplateAdditionalColumnType.indianPhoneNumber: {
//             return SchemaPropertyType.string;
//         }
//         case ProductCatalogueTemplateAdditionalColumnType.number: {
//             return SchemaPropertyType.number;
//         }
//         case ProductCatalogueTemplateAdditionalColumnType.select: {
//             return SchemaPropertyType.uuid;
//         }
//         case ProductCatalogueTemplateAdditionalColumnType.text: {
//             return SchemaPropertyType.string;
//         }
//         // TODO: Revisit this
//         case ProductCatalogueTemplateAdditionalColumnType.unknown: {
//             return SchemaPropertyType.string;
//         }
//         case ProductCatalogueTemplateAdditionalColumnType.uuid: {
//             return SchemaPropertyType.uuid;
//         }
//         default: {
//             throw new Error(`Unsupported product catalogue template additional column type: ${String(additionalColumnType)}`);
//         }
//     }
// }

// export function getSchemaPropertiesFromProductCatalogueTemplateAdditionalColumns(
//     additionalColumns: Array<ProductCatalogueTemplateAdditionalColumn>,
//     //
// ): Record<string, SchemaProperty>
//  {
//     const properties: Record<string, SchemaProperty> = {};
//     for (const [key, value] of Object.entries(productCataloguesWorkspaceCapabilityDefaultColumns)) {
//         properties[key] = {
//             id: generateUuid(),
//             ...value,
//         };
//     }

//     for (const additionalColumn of additionalColumns) {
//         const columnNameInSnakeCase = toSnakeCase(additionalColumn.displayName);
//         if (properties[columnNameInSnakeCase] !== undefined) {
//             throw new Error(`Duplicate additional column display name: ${additionalColumn.displayName}`);
//         }
//         properties[columnNameInSnakeCase] = {
//             displayName: additionalColumn.displayName,
//             id: additionalColumn.id,
//             internalName: toCamelCase(additionalColumn.displayName),
//             required: additionalColumn.configuration.required,
//             type: getSchemaPropertyTypeFromProductCatalogueTemplateAdditionalColumnType(additionalColumn.type),
//         };
//     }
//     return properties;
// }
